import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Search, Eye, EyeOff, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const ClientsList = ({ user, onStatsUpdate }) => {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const navigate = useNavigate();
  const [hiding, setHiding] = useState(false);


  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axiosInstance.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (clientId, clientName) => {
    setClientToDelete({ id: clientId, name: clientName });
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!clientToDelete) return;

    try {
      await axiosInstance.delete(`${API}/clients/${clientToDelete.id}`);
      toast.success('Manager deleted successfully');
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      fetchClients();
      onStatsUpdate();
    } catch (error) {
      console.error('Failed to delete manager:', error);
      toast.error('Failed to delete manager');
    }
  };

  const filteredClients = clients.filter((client) =>
    client.client_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-slate-300">Loading clients...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold title-gold">All Managers</h2>
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search managers..."
            className="pl-10 input-dark"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-clients-input"
          />
        </div>
      </div>

      {filteredClients.length === 0 ? (
        <Card className="beast-card">
          <CardContent className="py-12 text-center">
            <p className="text-slate-300" data-testid="no-clients-msg">
              {searchTerm ? 'No managers found matching your search.' : 'No managers added yet.'}
            </p>
            {user.role === 'admin' && !searchTerm && (
              <Button
                onClick={() => navigate('/add-client')}
                className="mt-4 btn-gold"
                data-testid="add-first-client-btn"
              >
                Add Your First Manager
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
          {filteredClients.map((client) => (
            <Card key={client.id} className="beast-card hover:shadow-2xl hover:shadow-yellow-900/20 transition-shadow duration-200" data-testid={`client-card-${client.id}`}>
              <CardHeader>
                <CardTitle className="text-lg text-slate-100" data-testid={`client-name-${client.id}`}>{client.client_name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Button
                    onClick={() => navigate(`/client/${client.id}`)}
                    className="flex-1 btn-gold"
                    data-testid={`view-client-btn-${client.id}`}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </Button>
                  {user.role === 'admin' && (
                    <>
                      <Button
                        onClick={async () => {
                          try {
                            setHiding(true);
                            await axiosInstance.post(`${API}/clients/${client.id}/hide`);
                            toast.success('Manager hidden');
                            fetchClients();
                            onStatsUpdate();
                          } catch (e) {
                            toast.error('Failed to hide manager');
                          } finally {
                            setHiding(false);
                          }
                        }}
                        className="btn-gold-outline"
                        size="icon"
                        title="Hide manager"
                        data-testid={`hide-client-btn-${client.id}`}
                        disabled={hiding}
                      >
                        <EyeOff className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => openDeleteDialog(client.id, client.client_name)}
                        className="btn-red"
                        size="icon"
                        data-testid={`delete-client-btn-${client.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="beast-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="title-gold">Are you sure?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              This will permanently delete <strong className="title-gold">{clientToDelete?.name}</strong> and all associated notes. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setClientToDelete(null)} className="btn-gold-outline">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="btn-red">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClientsList;